import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, Mail, Send, ChevronRight, Loader2, CheckCircle2, AlertCircle,
  User, Eye, Bold, Italic, Underline, Link2, List, ListOrdered, Type
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { sendOutreachEmails } from './coresignalApi';

/**
 * OutreachEmailModal — Premium email composer for candidate outreach.
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - candidates: [{ id, full_name, email, company_name, active_experience_title }]
 *  - jobTitle: string (from filters)
 *  - company: string (organization name)
 */
const OutreachEmailModal = ({ isOpen, onClose, candidates, jobTitle, company, sessionId }) => {
  const [step, setStep] = useState('compose'); // 'compose' | 'sending' | 'done'
  const [fromEmail, setFromEmail] = useState('recruiter@talentcrux.ai');
  const [subject, setSubject] = useState(
    `Opportunity: {{jobTitle}} at {{Company}}`
  );
  const [body, setBody] = useState(
    `Hi {{firstName}},\n\nI’m reaching out from {{Company}} because your background looked relevant for a {{jobTitle}} opening on our team.\n\nWe’re hiring for someone who can make an immediate impact. This is a hands-on role for someone who enjoys building, maintaining, and improving systems rather than staying at the high-level only.\n\nWhat makes the opportunity interesting is the chance to work closely on implementation, own meaningful work, and help shape how things are built as the team grows.\n\n• Strong emphasis on real ownership\n• Ideal for someone who likes shipping practical, production-ready solutions\n\nWe’re especially interested in professionals who can contribute quickly and bring strong hands-on depth to our ecosystem.\n\nIf that sounds potentially relevant, please reply with 'INTERESTED' if you would like to proceed.\n\nBest,\nTalentCrux Team`
  );
  const [previewIdx, setPreviewIdx] = useState(0);
  const [sendResults, setSendResults] = useState(null);

  const eligibleCandidates = candidates.filter(c => c.email);
  const noEmailCandidates = candidates.filter(c => !c.email);

  const previewCandidate = eligibleCandidates[previewIdx] || eligibleCandidates[0];

  const getPreviewText = (template, candidate) => {
    if (!candidate) return template;
    const firstName = candidate.full_name?.split(' ')[0] || 'Candidate';
    return template
      .replace(/\{\{firstName\}\}/gi, firstName)
      .replace(/\{\{First Name\}\}/gi, firstName)
      .replace(/\{\{jobTitle\}\}/gi, jobTitle || 'the position')
      .replace(/\{\{company\}\}/gi, company || 'our organization')
      .replace(/\{\{Company\}\}/gi, company || 'our organization');
  };

  const handleSend = async () => {
    setStep('sending');
    try {
      const recipients = eligibleCandidates.map(c => ({
        id: c.id,
        email: c.email,
        firstName: c.full_name?.split(' ')[0] || 'Candidate',
        jobTitle: jobTitle || 'the position',
        company: company || 'our organization',
      }));
      const result = await sendOutreachEmails(recipients, subject, body, fromEmail, sessionId);
      setSendResults(result);
      setStep('done');
    } catch (err) {
      console.error('Outreach send failed:', err);
      setSendResults({ totalSent: 0, totalFailed: eligibleCandidates.length, error: err.message });
      setStep('done');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-white to-[#f8faff]">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#007BFF] to-[#6C5CE7] flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                Notify Shortlisted Candidates
              </h2>
              <p className="text-xs text-gray-500 mt-0.5 ml-10">
                {eligibleCandidates.length} candidate{eligibleCandidates.length !== 1 ? 's' : ''} selected
                {noEmailCandidates.length > 0 && (
                  <span className="text-amber-600"> — {noEmailCandidates.length} without email (skipped)</span>
                )}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {step === 'compose' && (
            <div className="flex-1 flex min-h-0 overflow-hidden">
              {/* LEFT: Compose */}
              <div className="flex-1 flex flex-col overflow-y-auto p-6 border-r border-gray-100">
                {/* From */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">From</label>
                  <input
                    type="email"
                    value={fromEmail}
                    onChange={e => setFromEmail(e.target.value)}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/10 outline-none transition-all bg-gray-50/50"
                  />
                </div>

                {/* Subject */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/10 outline-none transition-all"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Use {'{{jobTitle}}'} and {'{{Company}}'} for job title and company
                  </p>
                </div>

                {/* Body */}
                <div className="flex-1 mb-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Email Body</label>
                  {/* Mini toolbar */}
                  <div className="flex items-center gap-0.5 px-2 py-1.5 border border-gray-200 border-b-0 rounded-t-xl bg-gray-50">
                    {[
                      { icon: Type, label: 'Normal' },
                      { icon: Bold, label: 'Bold' },
                      { icon: Italic, label: 'Italic' },
                      { icon: Underline, label: 'Underline' },
                    ].map(t => (
                      <button key={t.label} className="p-1.5 hover:bg-white rounded-md transition-colors" title={t.label}>
                        <t.icon className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    ))}
                    <div className="w-px h-5 bg-gray-200 mx-1" />
                    {[List, ListOrdered, Link2].map((Icon, i) => (
                      <button key={i} className="p-1.5 hover:bg-white rounded-md transition-colors">
                        <Icon className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    rows={10}
                    className="w-full px-3.5 py-3 border border-gray-200 rounded-b-xl text-sm text-gray-800 focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/10 outline-none transition-all resize-none leading-relaxed"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Use {'{{firstName}}'} — replaced per candidate. Use {'{{jobTitle}}'} and {'{{Company}}'} for job title and company.
                  </p>
                </div>
              </div>

              {/* RIGHT: Preview */}
              <div className="w-[380px] flex-shrink-0 flex flex-col bg-[#fafbfd] overflow-y-auto scrollbar-hide">
                <div className="px-5 pt-5 pb-3 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Preview as</span>
                  </div>
                  {eligibleCandidates.length > 1 && (
                    <select
                      value={previewIdx}
                      onChange={e => setPreviewIdx(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 bg-white focus:border-[#007BFF] outline-none"
                    >
                      {eligibleCandidates.map((c, i) => (
                        <option key={c.id} value={i}>{c.full_name || 'Unknown'}</option>
                      ))}
                    </select>
                  )}
                  {eligibleCandidates.length === 1 && previewCandidate && (
                    <div className="px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 bg-white">
                      {previewCandidate.full_name}
                    </div>
                  )}
                </div>

                {/* Email preview card */}
                {previewCandidate && (
                  <div className="mx-5 mb-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-shrink-0">
                    <div className="px-4 py-3 border-b border-gray-100 space-y-1">
                      <p className="text-[11px] text-gray-400">
                        <span className="font-semibold text-gray-600">To:</span> {previewCandidate.email}
                      </p>
                      <p className="text-[11px]">
                        <span className="font-semibold text-[#007BFF]">Subject:</span>{' '}
                        <span className="text-gray-700">{getPreviewText(subject, previewCandidate)}</span>
                      </p>
                    </div>
                    <div className="px-4 py-4 text-[13px] text-gray-700 leading-relaxed whitespace-pre-line">
                      {getPreviewText(body, previewCandidate)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'sending' && (
            <div className="flex-1 flex flex-col items-center justify-center py-16 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#007BFF] to-[#6C5CE7] flex items-center justify-center">
                  <Send className="w-7 h-7 text-white" />
                </div>
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  animate={{ boxShadow: ['0 0 0 0 rgba(0,123,255,0.3)', '0 0 0 16px rgba(0,123,255,0)'] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              </div>
              <p className="text-base font-bold text-gray-800">Sending outreach emails...</p>
              <p className="text-sm text-gray-500">
                Sending to {eligibleCandidates.length} candidate{eligibleCandidates.length !== 1 ? 's' : ''}
              </p>
              <Loader2 className="w-5 h-5 text-[#007BFF] animate-spin mt-2" />
            </div>
          )}

          {step === 'done' && sendResults && (
            <div className="flex-1 flex flex-col items-center justify-center py-16 gap-4">
              {sendResults.totalFailed === 0 ? (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                  </div>
                  <p className="text-base font-bold text-gray-800">All emails sent successfully!</p>
                  <p className="text-sm text-gray-500">{sendResults.totalSent} candidate{sendResults.totalSent !== 1 ? 's' : ''} notified</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    {sendResults.totalSent > 0 ? 'Partially sent' : 'Sending failed'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {sendResults.totalSent} sent, {sendResults.totalFailed} failed
                  </p>
                  {sendResults.error && (
                    <p className="text-xs text-red-500 bg-red-50 px-4 py-2 rounded-lg max-w-md text-center">{sendResults.error}</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-[#fafbfd]">
            <button
              onClick={() => { if (step === 'done') { onClose(); setStep('compose'); setSendResults(null); } else onClose(); }}
              className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {step === 'done' ? 'Close' : 'Cancel'}
            </button>
            {step === 'compose' && (
              <button
                onClick={handleSend}
                disabled={eligibleCandidates.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#007BFF] to-[#0062CC] text-white text-sm font-bold rounded-xl hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Send <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OutreachEmailModal;
